import React, { useEffect, useState, useRef } from "react";
import Choices from "choices.js";
import axios from "axios";
import { useGlobalStateStore } from "../../stores/store";
import { API_URL } from "../../constants";

const CountrySelect = ({ label = "Country" }) => {
  const [countries, setCountries] = useState([]);
  const selectRef = useRef(null);
  const choicesRef = useRef(null);
  const { filterOpen } = useGlobalStateStore(); // Access filterOpen state
  const store = useGlobalStateStore();
  const selectedCountries = useGlobalStateStore(
    (state) => state.filter.countries
  );

  const onChange = () => {
    const ref = selectRef.current;
    const selectedValues = ref
      ? Array.from(ref.selectedOptions).map((option) => option.value)
      : [];
    store.setCountry(selectedValues);

    const wrapper = ref.closest(".choices");
    if (wrapper) {
      wrapper.classList.toggle("has-values", selectedValues.length > 0);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchCountries = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/v1/countries/`);
        if (isMounted) {
          const countryList = response.data.map((country) => ({
            value: country.code,
            label: country.name,
          }));
          setCountries(countryList);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch countries:", error);
        }
      }
    };

    if (filterOpen) {
      fetchCountries(); // Fetch only when filter panel is open
    }

    return () => {
      isMounted = false; // Prevent state updates after unmount
    };
  }, [filterOpen]); // Re-run when filterOpen changes

  useEffect(() => {
    if (selectRef.current && countries.length > 0) {
      if (choicesRef.current) {
        choicesRef.current.destroy();
      }

      const choices = new Choices(selectRef.current, {
        searchEnabled: true,
        removeItemButton: true,
        placeholder: true,
        placeholderValue: "Select",
        noResultsText: "No countries found",
        noChoicesText: "No options available",
        itemSelectText: "",
        shouldSort: false,
        searchFields: ["label"],
        fuseOptions: {
          threshold: 0.1,
          ignoreLocation: false,
          location: 0,
          distance: 0,
          minMatchCharLength: 1,
        },
        searchPlaceholderValue: "Type to search...",
        searchResultLimit: 1000,
        searchChoices(value) {
          if (!value) return this._store.choices.filter((item) => item.active);
          return this._store.choices.filter(
            (item) =>
              item.active &&
              item.label.toLowerCase().startsWith(value.toLowerCase())
          );
        },
      });

      choicesRef.current = choices;
      choices.setChoices(countries, "value", "label", true);

      setTimeout(() => {
        choices.removeActiveItems();
        selectedCountries.forEach((code) => {
          choices.setChoiceByValue(code);
        });

        const wrapper = selectRef.current.closest(".choices");
        if (wrapper) {
          wrapper.classList.toggle("has-values", selectedCountries.length > 0);
        }
      }, 0);

      const dropdown =
        selectRef.current.parentElement.parentElement.querySelector(
          ".choices__list--dropdown"
        );
      const searchInput = selectRef.current.parentElement.querySelector(
        "input.choices__input"
      );

      if (dropdown && searchInput) {
        dropdown.insertBefore(searchInput, dropdown.firstChild);

        dropdown.addEventListener("focusout", (event) => {
          if (!dropdown.contains(event.relatedTarget)) {
            searchInput.value = "";
            const inputEvent = new Event("input", { bubbles: true });
            searchInput.dispatchEvent(inputEvent);
          }
        });

        const choicesContainer = selectRef.current.closest(".choices");
        const innerContainer =
          choicesContainer.querySelector(".choices__inner");

        if (innerContainer) {
          innerContainer.addEventListener("click", (event) => {
            const dropdownIsOpen =
              choicesContainer.classList.contains("is-open");

            document
              .querySelectorAll(".choices.is-open")
              .forEach((openDropdown) => {
                if (openDropdown !== choicesContainer) {
                  openDropdown.classList.remove("is-open");
                  const otherDropdownList = openDropdown.querySelector(
                    ".choices__list--dropdown"
                  );
                  if (otherDropdownList) {
                    otherDropdownList.classList.remove("is-active");
                  }
                }
              });

            if (!dropdownIsOpen) {
              choices.showDropdown();
              dropdown.classList.add("is-active");
            } else {
              choices.hideDropdown();
              dropdown.classList.remove("is-active");
            }

            event.stopPropagation();
          });
        }
      }
    }

    return () => {
      if (choicesRef.current) {
        choicesRef.current.destroy();
        choicesRef.current = null;
      }
    };
  }, [countries, selectedCountries]);

  return (
    <div className="filter-select-wrap">
      <label htmlFor="countrySelect">{label}</label>
      <select
        id="countrySelect"
        multiple
        ref={selectRef}
        onChange={onChange}
        className="display-content"
      ></select>
    </div>
  );
};

export default CountrySelect;
